import { listerBack } from '@/shared/utils/backListener';
import router from '@/router';
import platform from '@/shared/utils/platform';
import store from '@/store';
import { RouterV3Enum } from '@/router/router-names';
import { changeTabbarShowStatus, getSkTabInfo, changeTabbarMaskStatus } from '@/api/native/container';
import { getTabIdByPath } from '@/shared/utils/getTabIdByPath/getTabIdByPath';
import { ubtSendError } from '@/shared/utils/ubt';
import { PreCreditUserSourceEnums, UserHomeSceneEnums, NormalPreCreditModelEnums } from '@/enums/home-type';
import { creditTaskSuspendSceneEnum } from '@/enums/autarkyUpgrade';
import { SK_NATIVE_CONTAINER_HOME_TABID } from './constant';
import { CreditedQuotaStatusEnum, UserCreditData } from './types';

/** 是否正在执行监听回调方法 */
let executingListenCallback = false;
/** 监听back返回首页回调事件 */
let lastClearBackListerExec: () => void;

export const homeListerBack = (
  callback: () => Promise<void>,
): void => {
  /** 清除上次的监听回调，避免重复监听 */
  if (typeof lastClearBackListerExec === 'function') {
    lastClearBackListerExec();
  }

  const { clearBackLister } = listerBack(async () => {
    if (router.app.$route.path === RouterV3Enum.home) {
      /** back回到首页时，若tabbar未展示，则调用bridge展示客户端tabbar */
      if (store.state.base.isWebviewSkNativeContainer) {
        if ((await getSkTabInfo(SK_NATIVE_CONTAINER_HOME_TABID)).haveShown) {
          changeTabbarShowStatus(true);
        }
      }

      /**
       * 回到首页，需要判断是否是wallet 借钱tab显示的时候 才去刷新页面数据
       * isFinanceTabShow === undefined或者true时，去刷新页面数据
       */
      if (platform.isIOS && store.state.base.isHybridAppMode && window.isFinanceTabShow === false) {
        return;
      }

      try {
        /** 避免重复执行监听回调方法：目前ios下，从非本项目的页面返回首页，会触发多次监听回调 */
        if (!executingListenCallback) {
          executingListenCallback = true;
          await callback();
        }
      } catch (error) {
        ubtSendError(error as Error);
      } finally {
        executingListenCallback = false;
      }
    }
  }, false);

  lastClearBackListerExec = clearBackLister;
};

/** 刷新组件实例key，触发组件重新渲染 */
export const refreshComponentInstanceKey = (): string => `${Date.now()}_${Math.floor(Math.random() * 100)}`;

/** 获取用户已授信的额度状态 */
export const getCreditedQuotaStatus = (
  userCreditData: UserCreditData,
): CreditedQuotaStatusEnum | undefined => {
  const { creditStatus, creditAmount, hasOrder } = userCreditData;
  if (!creditStatus) return undefined;

  /** 当用户额度为0时，分为：额度已用完、额度未使用(已过期) */
  if (!Number(creditAmount)) {
    return hasOrder
      ? CreditedQuotaStatusEnum.USE_OUT_AMOUNT : CreditedQuotaStatusEnum.NEVER_USED_AMOUNT;
  }

  return CreditedQuotaStatusEnum.AMOUNT_NORMAL;
};

/** 获取额度说明文案 */
export const getMajorQuotaText = (
  userCreditData: UserCreditData,
): string => {
  const { preCreditUserSource, creditStatus } = userCreditData || {};
  /** APP圈客预授信、零用钱授信、海消用户，展示“参考可借额度” */
  if (
    [
      PreCreditUserSourceEnums.NORMAL,
      PreCreditUserSourceEnums.POCKET_MONEY,
      PreCreditUserSourceEnums.SEA_CONSUMER,
    ].includes(preCreditUserSource)
  ) {
    return '参考可借额度(元)';
  }

  /** 协议页流失用户预授信，展示“可提现额度” */
  if (
    [
      PreCreditUserSourceEnums.ADVERTISING,
      PreCreditUserSourceEnums.OLD_CUSTOMER,
      PreCreditUserSourceEnums.NO_ADVERTISING_NEW_CUSTOMER,
    ].includes(preCreditUserSource)
  ) {
    return '可提现额度(元)';
  }

  /** 已授信用户 && 非额度过期失效用户，展示“可提现额度” */
  if (creditStatus
    && getCreditedQuotaStatus(userCreditData) !== CreditedQuotaStatusEnum.NEVER_USED_AMOUNT) {
    return '可提现额度(元)';
  }

  return '最高可借额度(元)';
};

/** 获取用户首页场景 */
export const getUserHomeScene = (
  userCreditData: UserCreditData,
): UserHomeSceneEnums => {
  const {
    preCredit, normalPreCreditModel, creditStatus,
  } = userCreditData;
  let scene = UserHomeSceneEnums.TAGLESS;

  if (normalPreCreditModel === NormalPreCreditModelEnums.POCKET_MONEY_LOAN) {
    scene = UserHomeSceneEnums.POCKET_MONEY;
  } else if (preCredit) {
    scene = UserHomeSceneEnums.PRE_CREDIT;
  } else if (creditStatus) {
    scene = UserHomeSceneEnums.QUALITY;
  }
  return scene;
};

/** 设置“容器化webview-tabbar遮罩层”状态 */
const setSkWebviewTabbarMaskStatus = {
  showSetting: false,
  hideSetting: false,
};
/** 容器化webview-tabbar遮罩层展示 */
export const showSkWebviewTabbarMask = async (): Promise<void> => {
  try {
    /** 避免重复设置 */
    if (!setSkWebviewTabbarMaskStatus.showSetting) {
      setSkWebviewTabbarMaskStatus.showSetting = true;

      await changeTabbarMaskStatus({
        show: true,
        duration: 50,
        opacity: 0.6,
        tabId: getTabIdByPath(),
      });
    }
  } catch (error) {
    ubtSendError(error as Error);
  } finally {
    setSkWebviewTabbarMaskStatus.showSetting = false;
  }
};

/** 容器化webview-tabbar遮罩层隐藏 */
export const hideSkWebviewTabbarMask = async (): Promise<void> => {
  try {
    /** 避免重复设置 */
    if (!setSkWebviewTabbarMaskStatus.hideSetting) {
      setSkWebviewTabbarMaskStatus.hideSetting = true;

      await changeTabbarMaskStatus({
        show: false,
        duration: 50,
        tabId: getTabIdByPath(),
      });
    }
  } catch (error) {
    ubtSendError(error as Error);
  } finally {
    setSkWebviewTabbarMaskStatus.hideSetting = false;
  }
};

/** 是否因ocr未补充，授信任务挂起 */
export const isCreditTaskOcrSuspend = (userCreditData: UserCreditData): boolean => {
  const { suspendScene } = userCreditData || {};
  return [
    creditTaskSuspendSceneEnum.AUTARKY_OCR_SUSPEND,
    creditTaskSuspendSceneEnum.NO_AUTARKY_OCR_SUSPEND,
  ].includes(suspendScene as creditTaskSuspendSceneEnum);
};
